import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DemoPersonaList from '../DemoPage/DemoPersonaList';
import DemoMagicBenefits from '../DemoPage/DemoMagicBenefits';
import ChatSettings from './ChatSettings';
import LiveChatInterface from './LiveChatInterface';
import DemoEDNLPanel from './DemoEDNLPanel';
import DemoHebbianPanel from './DemoHebbianPanel';
import './DemoLivePage.css';
import api from '../../services/api';

const DemoLivePage = () => {
  // Persona and scenario state (from original demo)
  const [personas, setPersonas] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [demoConfig, setDemoConfig] = useState(null);
  const [selectedPersonaId, setSelectedPersonaId] = useState('harris');
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pipeline settings state
    const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('demoLiveSettings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
      }
    }
    return {
      execution_mode: 'BUILD_AND_RUN',
      workspace_id: '',
      graph_id: '',
      min_similarity: 0.5,
      snn_execution_mode: 'compiled_full',
      similarity_top_k: 150,
      decompose_query: true,
      nl_prompt: '',
    };
  });

  // Workspaces and graphs for dropdowns
  const [workspaces, setWorkspaces] = useState([]);
  const [graphs, setGraphs] = useState([]);

  // Fetch demo data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [personasRes, scenariosRes, configRes] = await Promise.all([
          api.get('/demo/personas'),
          api.get('/demo/scenarios'),
          api.get('/demo/config').catch(() => ({ data: null })),
        ]);
        
        setPersonas(personasRes.data);
        setScenarios(scenariosRes.data);
        if (configRes.data) setDemoConfig(configRes.data);
        
        const harrisScenarios = scenariosRes.data
          .filter(s => s.primaryPersonaId === 'harris')
          .sort((a, b) => a.order - b.order);
        
        if (harrisScenarios.length > 0) {
          setSelectedScenarioId(harrisScenarios[0].id);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching demo data:', err);
        setError('Failed to load demo data. Please refresh the page.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch workspaces and graphs for settings dropdowns
  useEffect(() => {
    const fetchWorkspacesAndGraphs = async () => {
      try {
        const [workspacesRes, graphsRes] = await Promise.all([
          api.listWorkspaces().catch(() => ({ data: [] })),
          api.listGraphs().catch(() => ({ data: [] }))
        ]);
        setWorkspaces(workspacesRes.data || []);
        setGraphs(graphsRes.data || []);
      } catch (err) {
        console.error('Error fetching workspaces/graphs:', err);
      }
    };

    fetchWorkspacesAndGraphs();
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('demoLiveSettings', JSON.stringify(settings));
  }, [settings]);

  // Auto-configure graph settings when scenario changes
  useEffect(() => {
    if (!demoConfig || !selectedScenarioId) return;
    const scenario = scenarios.find(s => s.id === selectedScenarioId);
    if (!scenario || !scenario.graphIds || !demoConfig.graphIds) return;

    const firstGraphKey = scenario.graphIds[0];
    const graphId = demoConfig.graphIds[firstGraphKey];
    if (graphId) {
      setSettings(prev => ({
        ...prev,
        workspace_id: demoConfig.workspaceId || prev.workspace_id,
        graph_id: graphId,
        snn_execution_mode: 'compiled_full',
      }));
    }
  }, [selectedScenarioId, demoConfig, scenarios]);

  // Handle persona selection
  const handlePersonaSelect = (personaId) => {
    setSelectedPersonaId(personaId);
    
    const personaScenarios = scenarios
      .filter(s => s.primaryPersonaId === personaId)
      .sort((a, b) => a.order - b.order);
    
    if (personaScenarios.length > 0) {
      setSelectedScenarioId(personaScenarios[0].id);
    }
  };

  // Handle settings change
  const handleSettingsChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Get current scenario for static sidebar
  const currentScenario = scenarios.find(s => s.id === selectedScenarioId);
  const selectedPersona = personas.find(p => p.id === selectedPersonaId);

  if (loading) {
    return (
      <div className="demo-live-page">
        <div className="demo-live-loading">Loading demo data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="demo-live-page">
        <div className="demo-live-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="demo-live-page">
      {/* Header */}
      <div className="demo-live-header">
        <Link to="/demo" className="demo-live-header-btn secondary">
          ← Back to Demo
        </Link>
        <div className="demo-live-title">CerebralMachines Live Chat</div>
        <span className="demo-live-badge">LIVE</span>
      </div>

      {/* Settings Panel */}
      <ChatSettings
        settings={settings}
        onSettingsChange={handleSettingsChange}
        workspaces={workspaces}
        graphs={graphs}
      />

      {/* Main Heading */}
      <div className="demo-live-main-heading">
        Interactive Chat with Platform Pipeline — Healthcare Demo
      </div>

      {/* Main Content - 3 Column Layout */}
      <div className="demo-live-content">
        {/* Left Column - Personas */}
        <div className="demo-live-column">
          <DemoPersonaList
            personas={personas}
            selectedPersonaId={selectedPersonaId}
            onPersonaSelect={handlePersonaSelect}
          />
        </div>

        {/* Center Column - Live Chat Interface */}
        <div className="demo-live-column">
          <LiveChatInterface
            settings={settings}
            persona={selectedPersona}
            scenario={currentScenario}
          />
        </div>

        {/* Right Column - Context panel (scenario-dependent) */}
        <div className="demo-live-column">
          {selectedScenarioId === 'ednl_clinical_events' ? (
            <DemoEDNLPanel workspaceId={settings.workspace_id} />
          ) : selectedScenarioId === 'adaptive_risk_prediction' ? (
            <DemoHebbianPanel
              graphId={settings.graph_id}
              workspaceId={settings.workspace_id}
            />
          ) : (
            <DemoMagicBenefits scenario={currentScenario} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoLivePage;
